import { useState } from "react";
import { toast } from "sonner";
import { Copy, MessageCircle, Plus, Trash2 } from "lucide-react";

export const HandoverCard = ({ template, onTemplate }) => {
  const [name, setName] = useState("");
  const link = `${window.location.origin}/thank-you`;
  const message = (template || "").replaceAll("{name}", name.trim() || "there").replaceAll("{link}", link);
  const copy = async () => {
    try { await navigator.clipboard.writeText(message); toast.success("Message copied"); } catch { toast.error("Could not copy. Select the text and copy it manually."); }
  };
  return (
    <section className="mb-6 border border-stone bg-paper p-6" data-testid="handover-card">
      <h2 className="font-serif text-2xl">Handover message</h2>
      <p className="mt-1 text-sm text-charcoal/75">Send this to a buyer after handover. Use {"{name}"} and {"{link}"} in the template; the link opens your private thank-you page.</p>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div className="space-y-4">
          <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Buyer's first name
            <input className="field mt-1.5 !py-2.5 text-sm normal-case tracking-normal" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. James" data-testid="handover-name" />
          </label>
          <label className="block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Template (saved with Save changes)
            <textarea rows={6} className="field mt-1.5 text-sm normal-case tracking-normal" value={template || ""} onChange={(e) => onTemplate(e.target.value)} data-testid="handover-template" />
          </label>
        </div>
        <div className="flex flex-col">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Ready to send</p>
          <p className="mt-1.5 flex-1 whitespace-pre-line border border-stone bg-[#FBF9F4] p-4 text-sm" data-testid="handover-preview">{message}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" onClick={copy} className="btn btn-primary !py-3" data-testid="handover-copy"><Copy className="h-4 w-4" />Copy message</button>
            <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer" className="btn btn-secondary !py-3" data-testid="handover-whatsapp"><MessageCircle className="h-4 w-4" />Open in WhatsApp</a>
          </div>
        </div>
      </div>
    </section>
  );
};

const FIELDS = [["name", "Name"], ["place", "Town"], ["source", "Source (e.g. Google)"]];

export const ReviewsCard = ({ reviews, onChange }) => {
  const list = Array.isArray(reviews) ? reviews : [];
  const update = (i, k, v) => onChange(list.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <section className="mb-6 border border-stone bg-paper p-6" data-testid="reviews-card">
      <h2 className="font-serif text-2xl">Customer reviews</h2>
      <p className="mt-1 text-sm text-charcoal/75">Add genuine reviews only, copied from Google with the customer's name as shown there. The "What customers say" section appears on the homepage as soon as one review is saved.</p>
      <div className="mt-5 space-y-4">
        {list.map((r, i) => (
          <div key={i} className="grid gap-2 border border-stone p-4 sm:grid-cols-3" data-testid={`review-row-${i}`}>
            {FIELDS.map(([k, l]) => <input key={k} aria-label={l} placeholder={l} className="field !py-2 text-sm" value={r[k] || ""} onChange={(e) => update(i, k, e.target.value)} data-testid={`review-${k}-${i}`} />)}
            <textarea aria-label="Review text" placeholder="Review text" rows={3} className="field text-sm sm:col-span-3" value={r.text || ""} onChange={(e) => update(i, "text", e.target.value)} data-testid={`review-text-${i}`} />
            <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="inline-flex items-center gap-1.5 justify-self-start text-sm text-[#8E2F25]" data-testid={`review-remove-${i}`}><Trash2 className="h-4 w-4" />Remove</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...list, { name: "", place: "", source: "Google", text: "" }])} className="btn btn-secondary mt-4 !py-2.5" data-testid="review-add"><Plus className="h-4 w-4" />Add review</button>
    </section>
  );
};
